import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import {
  HOUR_REPORTING_WEEKDAYS,
  HourReportingCompanyOverview,
  HourReportingDefaults,
  HourReportingDefaultsPayload,
  HourReportingDefaultsSummary,
  HourReportingWeekdayKey,
  listHourReportingDefaultsOverview,
  updateCompanyHourReportingDefaults,
  updateProjectHourReportingDefaults,
} from '../api/hourReportingDefaultsApi';

const WEEKDAY_LABELS: Record<HourReportingWeekdayKey, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

type EditableWeekdayState = Record<
  HourReportingWeekdayKey,
  {
    startTime: string;
    endTime: string;
    breakMinutes: string;
  }
>;

function createEditableState(defaults: HourReportingDefaults | null | undefined): EditableWeekdayState {
  return HOUR_REPORTING_WEEKDAYS.reduce((accumulator, weekday) => {
    const value = defaults?.weekdays?.[weekday];
    accumulator[weekday] = {
      startTime: value?.startTime || '',
      endTime: value?.endTime || '',
      breakMinutes: value?.breakMinutes === null || value?.breakMinutes === undefined ? '' : String(value.breakMinutes),
    };
    return accumulator;
  }, {} as EditableWeekdayState);
}

function toPayload(state: EditableWeekdayState): HourReportingDefaultsPayload {
  return HOUR_REPORTING_WEEKDAYS.reduce<HourReportingDefaultsPayload>((accumulator, weekday) => {
    const row = state[weekday];
    const startTime = row.startTime.trim() || null;
    const endTime = row.endTime.trim() || null;
    const breakMinutes = row.breakMinutes.trim() === '' ? null : Number(row.breakMinutes);
    accumulator[weekday] = startTime === null && endTime === null && breakMinutes === null
      ? null
      : {
          startTime,
          endTime,
          breakMinutes: Number.isFinite(breakMinutes) ? breakMinutes : null,
        };
    return accumulator;
  }, {});
}

function formatDefaultsSummary(summary: HourReportingDefaultsSummary): string {
  if (!summary.configuredWeekdays) {
    return 'No weekday defaults';
  }

  return `${summary.configuredWeekdays}/7 weekdays configured`;
}

function formatPlacementChipLabel(label: string, workerName: string | null): string {
  return workerName ? `${workerName} · ${label}` : label;
}

type DefaultsEditorProps = {
  title: string;
  subtitle: string;
  defaults: HourReportingDefaults | null;
  disabled?: boolean;
  onSave: (payload: HourReportingDefaultsPayload) => Promise<void>;
};

const DefaultsEditor: React.FC<DefaultsEditorProps> = ({
  title,
  subtitle,
  defaults,
  disabled = false,
  onSave,
}) => {
  const [draft, setDraft] = useState<EditableWeekdayState>(() => createEditableState(defaults));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setDraft(createEditableState(defaults));
  }, [defaults]);

  const configuredPreview = useMemo(() => {
    return HOUR_REPORTING_WEEKDAYS
      .map((weekday) => {
        const row = draft[weekday];
        if (!row.startTime && !row.endTime && row.breakMinutes === '') {
          return null;
        }
        const timeRange = row.startTime && row.endTime ? `${row.startTime}-${row.endTime}` : row.startTime || row.endTime || 'time';
        const breakLabel = row.breakMinutes !== '' ? ` / break ${row.breakMinutes}m` : '';
        return `${WEEKDAY_LABELS[weekday]} ${timeRange}${breakLabel}`;
      })
      .filter(Boolean)
      .join(' • ');
  }, [draft]);

  const updateField = (weekday: HourReportingWeekdayKey, field: 'startTime' | 'endTime' | 'breakMinutes', value: string) => {
    setDraft((current) => ({
      ...current,
      [weekday]: {
        ...current[weekday],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave(toPayload(draft));
    } catch (error: any) {
      setSaveError(error?.message || 'Failed to save defaults');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSave}
          disabled={disabled || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save defaults'}
        </Button>
      </Stack>

      {saveError && <Alert severity="error" sx={{ mt: 1.5 }}>{saveError}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        {configuredPreview || 'No weekday values set yet.'}
      </Typography>

      <Box
        sx={{
          mt: 2,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'repeat(7, minmax(0, 1fr))' },
          gap: 1.25,
        }}
      >
        {HOUR_REPORTING_WEEKDAYS.map((weekday) => (
          <Paper
            key={weekday}
            variant="outlined"
            sx={{
              p: 1.25,
              borderRadius: 2,
              backgroundColor: '#faf6eb',
              borderColor: '#e6dcc3',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#8c5a1f' }}>
              {WEEKDAY_LABELS[weekday]}
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              <TextField
                label="Start"
                type="time"
                size="small"
                value={draft[weekday].startTime}
                onChange={(event) => updateField(weekday, 'startTime', event.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={disabled || isSaving}
              />
              <TextField
                label="End"
                type="time"
                size="small"
                value={draft[weekday].endTime}
                onChange={(event) => updateField(weekday, 'endTime', event.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={disabled || isSaving}
              />
              <TextField
                label="Break (min)"
                type="number"
                size="small"
                value={draft[weekday].breakMinutes}
                onChange={(event) => updateField(weekday, 'breakMinutes', event.target.value)}
                inputProps={{ min: 0, step: 5 }}
                disabled={disabled || isSaving}
              />
            </Stack>
          </Paper>
        ))}
      </Box>
    </Paper>
  );
};

const CompanySection: React.FC<{
  company: HourReportingCompanyOverview;
  onSaveCompany: (businessId: string, payload: HourReportingDefaultsPayload) => Promise<void>;
  onSaveProject: (aanvraagNummer: number, payload: HourReportingDefaultsPayload) => Promise<void>;
}> = ({ company, onSaveCompany, onSaveProject }) => {
  return (
    <Accordion disableGutters sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #dccda8', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreOutlinedIcon />}>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <ApartmentOutlinedIcon fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {company.label}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {company.easyflexRelatieRegistratienummer
                  ? `Relation ${company.easyflexRelatieRegistratienummer}`
                  : 'No linked centraldb company record'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip icon={<WorkOutlineOutlinedIcon />} label={`${company.activePlacementCount} active placements`} />
              <Chip icon={<AccessTimeOutlinedIcon />} label={formatDefaultsSummary(company.summary)} />
              <Chip size="small" color={company.summary.hasStartTimeDefaults ? 'success' : 'default'} label={`Start ${company.summary.weekdaysWithStartTime}/7`} />
              <Chip size="small" color={company.summary.hasEndTimeDefaults ? 'success' : 'default'} label={`End ${company.summary.weekdaysWithEndTime}/7`} />
              <Chip size="small" color={company.summary.hasBreakDefaults ? 'success' : 'default'} label={`Break ${company.summary.weekdaysWithBreakMinutes}/7`} />
            </Stack>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: '#fffaf0' }}>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Active placements
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
              {company.activePlacements.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No active placements linked to this company.
                </Typography>
              ) : company.activePlacements.map((placement) => (
                <Chip
                  key={placement.id}
                  variant="outlined"
                  label={formatPlacementChipLabel(placement.label, placement.workerName)}
                />
              ))}
            </Stack>
          </Paper>

          <DefaultsEditor
            title="Company defaults"
            subtitle="Project-level values override company values for the same weekday."
            defaults={company.defaults}
            disabled={!company.editable || !company.businessId}
            onSave={(payload) => company.businessId ? onSaveCompany(company.businessId, payload) : Promise.resolve()}
          />

          <Divider />

          <Stack spacing={1.5}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Projects
            </Typography>
            {company.projects.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                <Typography variant="body2" color="text.secondary">
                  No active project-linked placements were found under this company.
                </Typography>
              </Paper>
            ) : company.projects.map((project) => (
              <Paper key={project.aanvraagNummer} variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FolderOutlinedIcon fontSize="small" />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {project.label}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {project.activePlacementCount} active placement(s)
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={formatDefaultsSummary(project.summary)} />
                    <Chip size="small" color={project.summary.hasStartTimeDefaults ? 'success' : 'default'} label={`Start ${project.summary.weekdaysWithStartTime}/7`} />
                    <Chip size="small" color={project.summary.hasEndTimeDefaults ? 'success' : 'default'} label={`End ${project.summary.weekdaysWithEndTime}/7`} />
                    <Chip size="small" color={project.summary.hasBreakDefaults ? 'success' : 'default'} label={`Break ${project.summary.weekdaysWithBreakMinutes}/7`} />
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                  {project.activePlacements.map((placement) => (
                    <Chip
                      key={placement.id}
                      size="small"
                      variant="outlined"
                      label={formatPlacementChipLabel(placement.label, placement.workerName)}
                    />
                  ))}
                </Stack>

                <Box sx={{ mt: 1.75 }}>
                  <DefaultsEditor
                    title="Project defaults"
                    subtitle="These values override the company defaults for matching weekdays."
                    defaults={project.defaults}
                    disabled={!project.editable}
                    onSave={(payload) => onSaveProject(project.aanvraagNummer, payload)}
                  />
                </Box>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const HourReportingDefaultsPage: React.FC = () => {
  const [overview, setOverview] = useState<HourReportingCompanyOverview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshOverview = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await listHourReportingDefaultsOverview();
      setOverview(response.items || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load hour-reporting defaults');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshOverview();
  }, []);

  const handleSaveCompany = async (businessId: string, payload: HourReportingDefaultsPayload) => {
    await updateCompanyHourReportingDefaults(businessId, payload);
    await refreshOverview();
  };

  const handleSaveProject = async (aanvraagNummer: number, payload: HourReportingDefaultsPayload) => {
    await updateProjectHourReportingDefaults(aanvraagNummer, payload);
    await refreshOverview();
  };

  return (
    <Box
      sx={{
        p: 2,
        minHeight: '100%',
        background: 'linear-gradient(180deg, #f6efe1 0%, #f7f6f1 100%)',
      }}
    >
      <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid #d9caa8', overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #eadfc8', backgroundColor: '#fff7e8' }}>
          <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: '#8c5a1f' }}>
            CentralDB Hour Reporting
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Company & Project Defaults
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 920, mt: 0.75 }}>
            Manage weekday-based default start time, end time, and breaks per company or per project. Project values override company values, and this page reads active placements directly from centraldb.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {isLoading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ p: 6 }}>
            <CircularProgress size={30} />
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ p: 2 }}>
            {overview.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, backgroundColor: '#fffdf8' }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  No active placements found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  The overview is driven by active centraldb placements. Once placements are active, their company and project rows will appear here.
                </Typography>
              </Paper>
            ) : overview.map((company) => (
              <CompanySection
                key={`${company.businessId || 'fallback'}-${company.label}`}
                company={company}
                onSaveCompany={handleSaveCompany}
                onSaveProject={handleSaveProject}
              />
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default HourReportingDefaultsPage;
